import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchMeet, type MeetResult } from '@/lib/meet';
import { TenantThemeProvider } from '@/lib/tenant';
import MeetYourLawyerCard from '@/components/MeetYourLawyer';

/**
 * Public "meet your lawyer" front door (accountable-attorney-primitive §5, P1-1).
 *
 * A zero-auth, zero-data-capture, deep-link-only page (mirrors VerifyEmail.tsx:
 * token in the path, no Nav entry, its own tiny surface). It fetches
 * GET /api/meet/:token and renders the SAME tenant-branded trust card the client
 * sees in-portal before Pay — the pre-pay standalone face of the primitive.
 *
 * The card renders UNDER THE TENANT'S brand (from the meet response's `tenant`
 * block, applied via <TenantThemeProvider>), not AVOWAL's. Graceful states:
 * loading / not-found / no-attorney-yet / error. There is no CTA that captures
 * data to VIEW the card; the "talk first" line is a callback note (the authed
 * request-call action lives in the portal twin).
 */
type Phase = 'loading' | 'ready' | 'not_found' | 'no_attorney' | 'error';

export default function MeetYourLawyer() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [result, setResult] = useState<Extract<MeetResult, { ok: true }> | null>(null);

  useEffect(() => {
    let live = true;
    setPhase('loading');
    setResult(null);
    fetchMeet(token ?? '').then((r) => {
      if (!live) return;
      if (r.ok) { setResult(r); setPhase('ready'); }
      else setPhase(r.reason);
    });
    return () => { live = false; };
  }, [token]);

  if (phase === 'ready' && result) {
    return (
      <TenantThemeProvider tenant={result.tenant}>
        <section className="container" style={{ maxWidth: '40rem', padding: '4rem 1.5rem 6rem' }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.28em',
            color: 'var(--cyan)', textTransform: 'uppercase', marginBottom: '1.5rem', textAlign: 'center',
          }}>
            / before you commit
          </div>
          <MeetYourLawyerCard
            data={result.meet}
            requestCall={{
              fallbackNote: 'Want to talk first? Your attorney can call you before you commit — reply to your invitation to set it up.',
            }}
          />
          <p style={{
            fontFamily: 'var(--font-serif)', fontSize: '0.9rem', color: 'var(--text-dim)',
            textAlign: 'center', marginTop: '1.5rem',
          }}>
            This page is just for you to meet your lawyer. Nothing is signed, paid, or filed here.
          </p>
        </section>
      </TenantThemeProvider>
    );
  }

  const copy: Record<Exclude<Phase, 'ready'>, { kicker: string; title: string; detail: string }> = {
    loading: {
      kicker: '/ loading',
      title: 'Loading your lawyer’s card…',
      detail: 'One moment while we pull up who’s handling your matter.',
    },
    not_found: {
      kicker: '/ link not found',
      title: 'This link isn’t active.',
      detail: 'A meet-your-lawyer link is good only until you sign or pay. It may have already been used, or the address was copied incompletely. Sign in to your portal to see your attorney there.',
    },
    no_attorney: {
      kicker: '/ almost ready',
      title: 'Your attorney’s card isn’t ready yet.',
      detail: 'The attorney on this matter is preparing their details. Check back shortly — you’ll be able to meet them, verify their bar number, and see the fee before anything is signed.',
    },
    error: {
      kicker: '/ hiccup',
      title: 'Could not load this page.',
      detail: 'Check your connection and reload — the link itself may still be good.',
    },
  };

  // `phase === 'ready'` is handled above; the fall-through only reaches the
  // non-ready states (this guards the edge where result is momentarily null).
  const { kicker, title, detail } = copy[phase === 'ready' ? 'loading' : phase];

  return (
    <section className="container" style={{ textAlign: 'center', padding: '8rem 2rem' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.28em',
        color: 'var(--cyan)', textTransform: 'uppercase', marginBottom: '1rem',
      }}>
        {kicker}
      </div>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2.6rem', letterSpacing: '0.02em' }}>
        {title}
      </h1>
      <p style={{
        fontFamily: 'var(--font-serif)', fontSize: '1.15rem', color: 'var(--text-dim)',
        marginTop: '1rem', marginBottom: '2rem', maxWidth: '34rem', marginLeft: 'auto', marginRight: 'auto',
      }}>
        {detail}
      </p>
      {phase !== 'loading' && (
        <Link to="/" style={{
          display: 'inline-block', padding: '0.85rem 1.5rem', background: 'var(--cyan)',
          color: 'var(--navy)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
          letterSpacing: '0.14em', textTransform: 'uppercase', borderRadius: 'var(--radius)', fontWeight: 600,
        }}>
          Back to home →
        </Link>
      )}
    </section>
  );
}
